import { useEffect, useId, useState } from "react";
import { AlertTriangle, ArrowDownUp, Clock3, KeyRound, Link2, Play, Plus, ShieldCheck } from "lucide-react";
import { SeriesChart, tone } from "../charts";
import { Badge, Button, Dialog, DialogActions, Field, FilterBar, Select, SummaryMetrics, Table } from "@studio";
import { SeoNotice } from "@studio-features/meta-crew/seo-shared";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { fmtDateTime, num } from "../lib/format";
import { keys, useAction, useCustomerDetail, useCustomers, useSettings } from "../lib/queries";
import {
  assetKindNames,
  connectionStatus,
  platformNames,
  platformOrder,
  SEO_POLICY_KEYS,
  type SeoAssetKind,
  type SeoConnection,
  type SeoPlatform,
  type SeoAsset,
  useSeoAssets,
  useSeoPostedBlogs,
  useSeoCollectFailures,
  useSeoCollectSummary,
  useSeoConnections,
  useSeoDailyUsage,
  useSeoUsage,
} from "../lib/seo-api";
import type { SeoCollectFailure as SeoCollectFailureRow } from "../lib/seo-api";
import type { Me } from "../lib/types";
import { Empty, ErrorBox, Loading, Panel, PanelError } from "../console/ui";
import { MetricGrid, type MetricItem } from "../console/metrics";

/**
 * SEO 수집 운영 (시안 e4ed46c seo-operations.tsx · seo-collection.tsx 를 실제 API 로).
 * 고객사 범위 화면(연결·자산·요약)에는 키 정보만. 사용량·한도·빌림/빌려줌은 모니터링 › SEO 수집에만 둔다.
 */

const inv = [["seo"]];

export function ConnectionBadge({ status }: { status: SeoConnection["status"] }) {
  const [label, tone] = connectionStatus[status] ?? [status, "slate"];
  return <Badge tone={tone}>{label}</Badge>;
}

/* ---------- 고객사 상세: 플랫폼별 상태·키 힌트 ---------- */

export function SeoConnectionSummary({ customerId, onOpen }: { customerId: string; onOpen?: () => void }) {
  // 2026-10-21 사용자: 플랫폼별 연결 상태를 KPI 카드와 같은 UI 로. 누르면 SEO › 연결 탭.
  const q = useSeoConnections(customerId);
  if (q.isLoading) return <Loading />;
  if (q.error) return <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  const rows = q.data ?? [];
  const items: MetricItem[] = platformOrder.map((p) => {
    const list = rows.filter((k) => k.platform === p);
    const best = list.find((k) => k.status === "ok") ?? list[0];
    const [label, tone] = best ? (connectionStatus[best.status] ?? [best.status, "neutral"]) : ["미등록", "neutral"];
    const hint = best?.credentialHint ? ` · •••• ${best.credentialHint.replace(/^…/, "")}` : "";
    return {
      label: platformNames[p],
      value: label,
      tone: tone === "slate" ? "neutral" : tone,
      note: list.length
        ? `키 ${list.length}개${hint}`
        : p === "google"
          ? "연결 등록만 가능 · 수집 준비 중"
          : "없으면 빌린 키·공용 연결로 수집",
      onOpen,
      openLabel: "SEO 연결 탭에서 보기",
    };
  });
  return <MetricGrid items={items} label="SEO 연결 상태" columns={3} />;
}

/* ---------- 연결 (고객사 / Meta Crew 공용): 키 정보만 ---------- */

type ConnDialog = { mode: "create" } | { mode: "replace" | "delete"; target: SeoConnection } | null;

export function SeoConnections({ me, customerId, initialCreate = false }: { me: Me; customerId: string | "shared"; initialCreate?: boolean }) {
  const canWrite = can(me, "MARKETING_ASSET_WRITE");
  const shared = customerId === "shared";
  const q = useSeoConnections(customerId);
  const [dialog, setDialog] = useState<ConnDialog>(initialCreate && canWrite ? { mode: "create" } : null);
  const [notice, setNotice] = useState("");
  const patch = useAction((v: { id: string; body: Record<string, unknown> }) => api.patch(`/admin/seo/connections/${v.id}`, v.body), inv);
  const test = useAction((id: string) => api.post<SeoConnection>(`/admin/seo/connections/${id}/test`), inv);
  const run = (p: Promise<unknown>, done: string) => p.then(() => setNotice(done)).catch((e) => setNotice(errorMessage(e)));
  const rows = q.data ?? [];
  return (
    <div className="seo-stack">
      {notice && (
        <SeoNotice>
          <span role="status">{notice}</span>
        </SeoNotice>
      )}
      <Panel
        title={shared ? "Meta Crew 공용 연결" : "플랫폼 연결"}
        extra={
          canWrite && (
            <Button size="sm" onClick={() => setDialog({ mode: "create" })}>
              <Plus size={14} />
              연결 등록
            </Button>
          )
        }
      >
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
        ) : rows.length ? (
          <Table>
            <thead>
              <tr>
                <th>플랫폼 / 이름</th>
                <th>키 끝자리</th>
                <th>상태</th>
                {!shared && <th>빌려주기</th>}
                <th>마지막 확인 / 사유</th>
                {canWrite && <th>관리</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.id}>
                  <td>
                    <span className="seo-key-title">
                      <KeyRound size={16} />
                      <strong>{platformNames[k.platform]}</strong>
                    </span>
                    <small className="seo-meta">{k.label}</small>
                    {k.platform !== "naver_search" && <small className="seo-meta">연결 등록 지원 · 수집 준비 중</small>}
                  </td>
                  <td>
                    <code>•••• {(k.credentialHint ?? "").replace(/^…/, "")}</code>
                  </td>
                  <td>
                    <ConnectionBadge status={k.status} />
                  </td>
                  {!shared && (
                    <td>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={k.shareable}
                        aria-label={`${k.label} 빌려주기`}
                        disabled={!canWrite || patch.isPending}
                        className="seo-toggle"
                        onClick={() => void run(patch.mutateAsync({ id: k.id, body: { shareable: !k.shareable } }), `${k.label}: 빌려주기를 ${k.shareable ? "막았습니다" : "허용했습니다"}.`)}
                      >
                        <span />
                        {k.shareable ? "허용" : "차단"}
                      </button>
                    </td>
                  )}
                  <td>
                    {k.lastCheckedAt ? fmtDateTime(k.lastCheckedAt) : "—"}
                    <small className="seo-meta">
                      {k.status === "quota" && k.quotaUntil ? `한도 초과 · ${fmtDateTime(k.quotaUntil)} 이후 다시 사용` : (k.lastError ?? "—")}
                    </small>
                  </td>
                  {canWrite && (
                    <td>
                      <div className="seo-actions">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={k.status === "disabled" || test.isPending}
                          onClick={() =>
                            void test
                              .mutateAsync(k.id)
                              .then((r) =>
                                setNotice(
                                  k.platform !== "naver_search"
                                    ? `${k.label}: 형식만 확인했습니다. 이 플랫폼의 수집은 준비 중입니다.`
                                    : r.status === "ok"
                                      ? `${k.label} 확인 완료`
                                      : `${k.label}: ${connectionStatus[r.status][0]}${r.lastError ? ` · ${r.lastError}` : ""}`,
                                ),
                              )
                              .catch((e) => setNotice(errorMessage(e)))
                          }
                        >
                          확인
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDialog({ mode: "replace", target: k })}>
                          키 교체
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void run(
                              patch.mutateAsync({ id: k.id, body: { enabled: k.status === "disabled" } }),
                              k.status === "disabled" ? `${k.label}을 다시 사용합니다 (확인 전).` : `${k.label} 사용을 중지했습니다.`,
                            )
                          }
                        >
                          {k.status === "disabled" ? "재개" : "중지"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDialog({ mode: "delete", target: k })}>
                          삭제
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="seo-ops-empty">
            <KeyRound size={28} />
            <strong>등록된 키가 없습니다</strong>
            <p>{shared ? "공용 연결은 키가 없거나 막힌 고객사의 마지막 대체 연결입니다." : "키가 없으면 다른 고객사가 빌려준 키나 Meta Crew 공용 연결로 수집합니다."}</p>
            {canWrite && (
              <Button variant="secondary" onClick={() => setDialog({ mode: "create" })}>
                첫 연결 등록
              </Button>
            )}
          </div>
        )}
        <div className="seo-table-foot">
          <p className="seo-caption">키 원문은 저장 후 다시 표시되지 않습니다. 검색광고·구글은 연결 등록만 지원하며 수집은 준비 중입니다.</p>
        </div>
      </Panel>
      <ConnectionDialog customerId={shared ? null : customerId} dialog={dialog} onClose={() => setDialog(null)} onDone={setNotice} />
    </div>
  );
}

function ConnectionDialog({ customerId, dialog, onClose, onDone }: { customerId: string | null; dialog: ConnDialog; onClose: () => void; onDone: (msg: string) => void }) {
  const formId = useId();
  const [platform, setPlatform] = useState<SeoPlatform>("naver_search");
  const [error, setError] = useState("");
  const target = dialog && dialog.mode !== "create" ? dialog.target : null;
  useEffect(() => {
    setError("");
    setPlatform(target?.platform ?? "naver_search");
  }, [dialog, target]);
  const create = useAction((body: Record<string, unknown>) => api.post<SeoConnection>("/admin/seo/connections", body), inv);
  const patch = useAction((v: { id: string; body: Record<string, unknown> }) => api.patch<SeoConnection>(`/admin/seo/connections/${v.id}`, v.body), inv);
  const del = useAction((id: string) => api.del(`/admin/seo/connections/${id}`), inv);
  const busy = create.isPending || patch.isPending || del.isPending;
  const mode = dialog?.mode;

  const submit = async (form: HTMLFormElement) => {
    if (mode === "delete" && target) {
      await del.mutateAsync(target.id);
      onDone(`${target.label} 연결을 삭제했습니다. 다음 수집부터 이 키를 쓰지 않습니다.`);
      return onClose();
    }
    const d = new FormData(form);
    const v = (n: string) => String(d.get(n) ?? "").trim();
    let credentials: Record<string, string>;
    if (platform === "naver_search") credentials = { clientId: v("clientId"), clientSecret: v("clientSecret") };
    else if (platform === "naver_searchad") credentials = { customerId: v("adCustomerId"), accessLicense: v("accessLicense"), secretKey: v("secretKey") };
    else {
      const raw = v("serviceAccountJson");
      try {
        const j = JSON.parse(raw) as Record<string, unknown>;
        if (j.type !== "service_account" || !j.client_email || !j.private_key) throw new Error();
      } catch {
        throw new Error("서비스 계정 JSON의 type, client_email, private_key를 확인해 주세요.");
      }
      credentials = { serviceAccountJson: raw };
    }
    const limits = { ...(Number(d.get("dailyLimit")) ? { dailyLimit: Number(d.get("dailyLimit")) } : {}), ...(Number(d.get("monthlyLimit")) ? { monthlyLimit: Number(d.get("monthlyLimit")) } : {}) };
    const label = v("label");
    if (!label) throw new Error("연결 이름을 입력해 주세요.");
    if (mode === "replace" && target) {
      const r = await patch.mutateAsync({ id: target.id, body: { label, credentials, ...limits } });
      onDone(`${label}: 키를 교체했습니다 · ${connectionStatus[r.status][0]}`);
    } else {
      const r = await create.mutateAsync({ ...(customerId ? { customerId } : {}), platform, label, credentials, shareable: customerId ? d.get("shareable") === "on" : true, ...limits });
      onDone(`${label} 연결을 저장했습니다 · ${connectionStatus[r.status][0]}${r.lastError && r.status !== "ok" ? ` · ${r.lastError}` : ""}`);
    }
    form.reset();
    onClose();
  };

  return (
    <Dialog
      open={!!dialog}
      onOpenChange={(v) => !v && onClose()}
      size="wide"
      title={mode === "delete" ? "연결 삭제" : mode === "replace" ? "API 키 교체" : "플랫폼 연결 등록"}
      description={customerId ? "고객사 전용 연결" : "고객사에 속하지 않는 Meta Crew 공용 연결"}
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: onClose }}
          primary={{ label: mode === "delete" ? "삭제" : "저장", type: "submit", form: formId, tone: mode === "delete" ? "danger" : "default", disabled: busy }}
          note={error || undefined}
        />
      }
    >
      <form
        id={formId}
        key={`${mode}-${target?.id ?? "new"}`}
        className="mcc-form"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          setError("");
          submit(e.currentTarget).catch((er) => setError(errorMessage(er)));
        }}
      >
        {mode === "delete" ? (
          <p>{target?.label} 연결을 삭제하면 다음 수집부터 이 키를 사용하지 않습니다. 기존 분석 결과는 유지됩니다.</p>
        ) : (
          <>
            <Select label="플랫폼" value={platform} disabled={mode === "replace"} onChange={(e) => setPlatform(e.target.value as SeoPlatform)}>
              {platformOrder.map((p) => (
                <option key={p} value={p}>
                  {platformNames[p]}
                </option>
              ))}
            </Select>
            <Field name="label" label="연결 이름" defaultValue={target?.label ?? ""} required maxLength={100} />
            <div key={platform} className="mcc-form">
              {platform === "naver_search" && (
                <>
                  <Field name="clientId" label="Client ID" autoComplete="off" required />
                  <Field name="clientSecret" label="Client Secret" type="password" autoComplete="new-password" required minLength={4} />
                </>
              )}
              {platform === "naver_searchad" && (
                <>
                  <Field name="adCustomerId" label="검색광고 고객 ID" autoComplete="off" required />
                  <Field name="accessLicense" label="액세스 라이선스" type="password" autoComplete="new-password" required />
                  <Field name="secretKey" label="비밀 키" type="password" autoComplete="new-password" required minLength={4} />
                </>
              )}
              {platform === "google" && (
                <label className="seo-json-label">
                  서비스 계정 JSON
                  <textarea name="serviceAccountJson" required rows={7} autoComplete="off" spellCheck={false} placeholder={'{"type":"service_account", ...}'} />
                </label>
              )}
            </div>
            {customerId && mode === "create" && (
              <label className="seo-checkbox">
                <input name="shareable" type="checkbox" defaultChecked={false} />
                다른 고객사의 수집에 빌려주기 허용
              </label>
            )}
            <details className="seo-values">
              <summary>한도 설정 (선택)</summary>
              <div className="seo-ops-form-grid">
                <Field label="일 호출 한도" name="dailyLimit" type="number" min={1} placeholder={platform === "naver_search" ? "기본 25,000" : "선택 입력"} />
                <Field label="월 호출 한도" name="monthlyLimit" type="number" min={1} placeholder={platform === "naver_search" ? "기본 775,000" : "선택 입력"} />
              </div>
              <p className="seo-caption">사용량과 한도 현황은 모니터링 › SEO 수집에서 확인합니다.</p>
            </details>
            {mode === "replace" && <p className="seo-caption">기존 키 원문은 표시되지 않습니다. 새 키를 입력하면 확인 전 상태로 돌아가고 바로 확인합니다.</p>}
          </>
        )}
      </form>
    </Dialog>
  );
}

/* ---------- 자산 (우리 항목 판별 기준) ---------- */

const ASSET_KINDS: SeoAssetKind[] = ["naver_blog", "naver_cafe", "site", "place", "shop_store"];

export function SeoAssetsPanel({ me, customerId }: { me: Me; customerId: string }) {
  const canWrite = can(me, "MARKETING_ASSET_WRITE");
  const q = useSeoAssets(customerId);
  const posted = useSeoPostedBlogs(customerId);
  const postedRows = posted.data?.items ?? [];
  const detail = useCustomerDetail(customerId);
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SeoAsset | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const update = useAction((v: { id: string; body: { label?: string; enabled?: boolean } }) => api.patch(`/admin/seo/customers/${customerId}/assets/${v.id}`, v.body), inv);
  const add = useAction((body: Record<string, unknown>) => api.post(`/admin/seo/customers/${customerId}/assets`, body), inv);
  const del = useAction((id: string) => api.del(`/admin/seo/customers/${customerId}/assets/${id}`), inv);
  const blogId = ((detail.data?.settings ?? {}) as { intro?: { blogId?: string } }).intro?.blogId;
  const rows = q.data?.items ?? [];
  return (
    <div className="seo-stack">
      <SeoNotice>블로그·카페 주소, 도메인, 업체명·판매처로 검색 결과의 우리 항목을 판별합니다. 테넌트 소개에 등록한 블로그 ID와, 이 고객사를 대상으로 게시한 폰(다른 고객사 폰 포함)의 블로그 ID는 자동 포함됩니다. 폰의 블로그 ID는 디바이스 화면에서 입력합니다.</SeoNotice>
      {notice && (
        <SeoNotice>
          <span role="status">{notice}</span>
        </SeoNotice>
      )}
      <Panel
        title="우리 항목 판별 기준"
        extra={
          canWrite && (
            <Button
              size="sm"
              onClick={() => {
                setError("");
                setEditing(null);
                setOpen(true);
              }}
            >
              <Plus size={14} />
              자산 추가
            </Button>
          )
        }
      >
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>종류</th>
                <th>판별 값 / 이름</th>
                <th>사용 여부</th>
                <th>출처</th>
                {canWrite && <th>관리</th>}
              </tr>
            </thead>
            <tbody>
              {blogId && (
                <tr>
                  <td>
                    <span className="seo-key-title">
                      <Link2 size={16} />
                      블로그
                    </span>
                  </td>
                  <td>
                    <strong>{blogId}</strong>
                    <small className="seo-meta">테넌트 소개의 블로그 ID</small>
                  </td>
                  <td>
                    <Badge tone="teal">사용</Badge>
                  </td>
                  <td>
                    <Badge>자동 포함</Badge>
                  </td>
                  {canWrite && (
                    <td>
                      <span className="seo-caption">테넌트 소개에서 관리</span>
                    </td>
                  )}
                </tr>
              )}
              {postedRows.map((b) => (
                <tr key={`posted-${b.blogId}-${b.executorName}-${b.deviceName}`}>
                  <td>
                    <span className="seo-key-title">
                      <Link2 size={16} />
                      블로그
                    </span>
                  </td>
                  <td>
                    <strong>{b.blogId}</strong>
                    <small className="seo-meta">
                      {b.executorName} · {b.deviceName ?? "디바이스"} · 게시 {b.posts}건
                    </small>
                  </td>
                  <td>
                    <Badge tone="teal">사용</Badge>
                  </td>
                  <td>
                    <Badge>게시 작업</Badge>
                  </td>
                  {canWrite && (
                    <td>
                      <span className="seo-caption">디바이스에서 관리</span>
                    </td>
                  )}
                </tr>
              ))}
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="seo-key-title">
                      <Link2 size={16} />
                      {assetKindNames[a.kind] ?? a.kind}
                    </span>
                  </td>
                  <td>
                    <strong>{a.matchKey}</strong>
                    <small className="seo-meta">{a.label ?? "—"}</small>
                  </td>
                  <td>
                    {canWrite ? (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={a.enabled}
                        aria-label={`${a.matchKey} 판별에 사용`}
                        disabled={update.isPending}
                        className="seo-toggle"
                        onClick={() =>
                          void update
                            .mutateAsync({ id: a.id, body: { enabled: !a.enabled } })
                            .then(() => setNotice(`${a.matchKey}: 판별에 ${a.enabled ? "쓰지 않습니다" : "다시 씁니다"}.`))
                            .catch((e) => setNotice(errorMessage(e)))
                        }
                      >
                        <span />
                        {a.enabled ? "사용" : "미사용"}
                      </button>
                    ) : (
                      <Badge tone={a.enabled ? "teal" : "slate"}>{a.enabled ? "사용" : "미사용"}</Badge>
                    )}
                  </td>
                  <td>관리자 등록</td>
                  {canWrite && (
                    <td>
                      <div className="seo-actions">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setError("");
                            setEditing(a);
                            setOpen(true);
                          }}
                        >
                          수정
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={del.isPending}
                          onClick={() =>
                            void del
                              .mutateAsync(a.id)
                              .then(() => setNotice(`${a.matchKey} 판별 기준을 삭제했습니다.`))
                              .catch((e) => setNotice(errorMessage(e)))
                          }
                        >
                          삭제
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!q.isLoading && !rows.length && !blogId && !postedRows.length && <div className="seo-ops-empty">등록된 판별 기준이 없습니다. 자산을 추가하거나 테넌트 소개에 블로그 ID를 입력해 주세요.</div>}
      </Panel>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? "SEO 자산 수정" : "SEO 자산 추가"}
        footer={
          <DialogActions
            secondary={{ label: "취소", onClick: () => setOpen(false) }}
            primary={{ label: "저장", type: "submit", form: formId, disabled: add.isPending || update.isPending }}
            note={error || undefined}
          />
        }
      >
        <form
          id={formId}
          key={editing?.id ?? "new"}
          className="mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            if (editing) {
              const label = String(d.get("label") ?? "").trim();
              const enabled = d.get("enabled") === "on";
              update
                .mutateAsync({ id: editing.id, body: { label, enabled } })
                .then(() => {
                  setNotice(`${editing.matchKey} 판별 기준을 수정했습니다.`);
                  setOpen(false);
                })
                .catch((er) => setError(errorMessage(er)));
              return;
            }
            const kind = String(d.get("kind")) as SeoAssetKind;
            const matchKey = String(d.get("matchKey") ?? "").trim();
            const label = String(d.get("label") ?? "").trim();
            if (!matchKey) return setError("판별 값을 입력해 주세요.");
            if (rows.some((a) => a.kind === kind && a.matchKey.toLowerCase() === matchKey.toLowerCase())) return setError("같은 기준은 중복 등록할 수 없습니다.");
            setError("");
            add
              .mutateAsync({ kind, matchKey, ...(label ? { label } : {}) })
              .then(() => {
                setNotice(`${assetKindNames[kind]} 판별 기준을 추가했습니다.`);
                setOpen(false);
              })
              .catch((er) => setError(errorMessage(er)));
          }}
        >
          <Select label="자산 종류" name="kind" defaultValue={editing?.kind ?? "naver_blog"} disabled={!!editing}>
            {ASSET_KINDS.map((k) => (
              <option key={k} value={k}>
                {assetKindNames[k]}
              </option>
            ))}
          </Select>
          <Field label="판별 값" name="matchKey" required maxLength={300} defaultValue={editing?.matchKey ?? ""} readOnly={!!editing} placeholder="블로그 주소·ID, 카페 주소, 도메인, 업체명 또는 판매처" />
          <Field label="자산 이름" name="label" maxLength={100} defaultValue={editing?.label ?? ""} />
          {editing && (
            <>
              <label className="seo-checkbox">
                <input type="checkbox" name="enabled" defaultChecked={editing.enabled} />
                판별에 사용
              </label>
              <p className="seo-caption">판별 값과 종류를 바꾸려면 삭제 후 새로 추가하세요.</p>
            </>
          )}
        </form>
      </Dialog>
    </div>
  );
}

/* ---------- 모니터링 › SEO 수집 ---------- */

const thisMonth = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 7);

function CallsChart({ customerId }: { customerId?: string }) {
  const q = useSeoDailyUsage(30, customerId);
  const rows = q.data?.items ?? [];
  const days = [...new Set(rows.map((r) => r.day))].sort();
  const data = days.map((day) => {
    const list = rows.filter((r) => r.day === day);
    const borrowed = list.reduce((s, r) => s + r.borrowed, 0);
    const calls = list.reduce((s, r) => s + r.calls, 0);
    return { day, own: calls - borrowed, borrowed, failed: list.reduce((s, r) => s + r.errors, 0) };
  });
  if (q.isLoading) return <Loading />;
  if (!data.length)
    return (
      <Panel title="일별 수집 호출" extra={<Badge>최근 30일 · KST</Badge>}>
        <Empty>최근 30일 수집 호출이 없습니다.</Empty>
      </Panel>
    );
  // 호출 수(자기 키·빌린 키 누적 막대)와 실패 수(선, 오른쪽 축)는 크기가 달라 축을 나눈다
  return (
    <SeriesChart
      title="일별 수집 호출"
      description="최근 30일 · KST · 막대 = 호출 수(자기 키 + 빌린 키), 선 = 실패 호출(오른쪽 축). 키 확인 호출은 포함하지 않습니다."
      kind="bar"
      stacked
      labels={data.map((d) => d.day.slice(5).replace("-", "."))}
      series={[
        { name: "자기 키", values: data.map((d) => d.own), color: tone.main },
        { name: "빌린 키", values: data.map((d) => d.borrowed), color: tone.muted },
        { name: "실패", values: data.map((d) => d.failed), color: tone.fail, type: "line", axis: "right" },
      ]}
    />
  );
}

function policyOf(settings: { key: string; value: unknown }[] | undefined) {
  const get = (k: string, d: number) => Number(settings?.find((s) => s.key === k)?.value ?? d);
  return {
    interval: get("seo_collect_interval_hours", 24),
    batch: get("seo_collect_batch", 200),
    depth: get("seo_collect_depth", 100),
    warn: get("seo_quota_warn_pct", 80),
    retention: get("seo_result_retention_days", 90),
  };
}

export function SeoCollectionScreen({ me, onOpenCustomer }: { me: Me; onOpenCustomer: (customerId: string) => void }) {
  const canRun = can(me, "COLLECTOR_MANAGE");
  const customers = useCustomers();
  const settings = useSettings();
  const summary = useSeoCollectSummary();
  const conns = useSeoConnections(null);
  const [customerId, setCustomerId] = useState("all");
  const [month, setMonth] = useState(thisMonth());
  const [sort, setSort] = useState<"calls" | "errors" | "name">("calls");
  const [desc, setDesc] = useState(true);
  const [notice, setNotice] = useState("");
  const usage = useSeoUsage(month);
  const failures = useSeoCollectFailures();
  const runNow = useAction(() => api.post<{ keywords: number; calls: number; failed: number; noConnection: number }>("/admin/seo/collect/run"), inv);
  const policy = policyOf(settings.data);
  const s = summary.data;
  const all = usage.data?.items ?? [];
  const filtered = all.filter((u) => customerId === "all" || u.customerId === customerId);
  const rows = [...filtered].sort(
    (a, b) => (desc ? -1 : 1) * (sort === "name" ? a.customerName.localeCompare(b.customerName, "ko") : sort === "errors" ? a.errorsMonth - b.errorsMonth : a.callsMonth - b.callsMonth),
  );
  const connections = (conns.data ?? []).filter((c) => customerId === "all" || c.customerId === customerId);
  const warnings = connections.filter((c) => c.usedMonthPct !== null && c.usedMonthPct >= policy.warn);
  const isCurrent = month === thisMonth();
  return (
    <div className="seo-stack">
      <FilterBar>
        <Select label="고객사" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="all">전체 고객사</option>
          {(customers.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <span className="seo-caption">오늘 · 한국 시간 기준 · 실시간 갱신</span>
        {canRun && (
          <Button
            disabled={runNow.isPending || !(s?.tracking ?? 0)}
            onClick={() =>
              void runNow
                .mutateAsync(undefined)
                .then((r) => setNotice(`수집을 한 번 실행했습니다 · 키워드 ${r.keywords}개 · 호출 ${r.calls}건 · 실패 ${r.failed}건 · 연결 없음 ${r.noConnection}건`))
                .catch((e) => setNotice(errorMessage(e)))
            }
          >
            <Play size={14} />
            {runNow.isPending ? "수집 중" : "지금 수집"}
          </Button>
        )}
      </FilterBar>
      {notice && (
        <SeoNotice>
          <span role="status">{notice}</span>
        </SeoNotice>
      )}
      {summary.error ? (
        <ErrorBox error={errorMessage(summary.error)} onRetry={() => void summary.refetch()} />
      ) : (
        <SummaryMetrics
          items={[
            { label: "추적 키워드", value: s ? num(s.tracking) : "…", note: "현재 추적 중 · 전체 고객사" },
            { label: "오늘 수집", value: s ? num(s.collectedToday) : "…", note: "오늘 수집한 키워드" },
            { label: "수집 실패 키워드", value: s ? num(s.failing) : "…", note: "마지막 수집 실패", tone: s?.failing ? "amber" : undefined },
            { label: "연결 정상 / 문제", value: s ? `${s.connectionsOk} / ${s.connectionsProblem}` : "…", note: `등록 연결 ${s?.connections ?? 0}개 · 키 거부·한도 초과`, tone: s?.connectionsProblem ? "coral" : "teal" },
          ]}
        />
      )}
      <div className="seo-routing">
        <Clock3 size={18} />
        <div>
          <strong>10분마다 최대 {num(policy.batch)}개 키워드</strong>
          <p>
            기본 {policy.interval}시간 주기 · 상위 {policy.depth}개 (지역 5개) · 결과 보관 {policy.retention}일
          </p>
          <small>자기 연결 → 같은 플랫폼의 빌려주기 허용 연결(남은 월 한도가 많은 순) → Meta Crew 공용 연결. 키 거부·한도 실패는 다음 연결로 한 번만 재시도합니다.</small>
        </div>
      </div>
      <FilterBar>
        <Field label="사용량 조회 월" type="month" max={thisMonth()} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <Select label="사용량 정렬" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="calls">수집 호출</option>
          <option value="errors">실패 호출</option>
          <option value="name">고객사</option>
        </Select>
        <Button variant="secondary" onClick={() => setDesc(!desc)}>
          <ArrowDownUp size={14} />
          {desc ? "내림차순" : "오름차순"}
        </Button>
      </FilterBar>
      <Panel title="연결 한도 경고" extra={<Badge tone={warnings.length ? "amber" : "teal"}>{warnings.length}건</Badge>}>
        {warnings.map((c) => (
          <div className="seo-warning-row" key={c.id}>
            <div>
              <strong>
                <AlertTriangle size={14} /> {c.customerName} · {c.label} · •••• {(c.credentialHint ?? "").replace(/^…/, "")}
              </strong>
              <p>
                {c.usedMonthPct}% 사용 · {num(c.usedMonth)} / {num(c.monthlyLimit)} · 경고 기준 {policy.warn}%
              </p>
              {c.monthlyLimit !== null && c.usedMonth >= c.monthlyLimit && <p>이 연결 사용 보류 · 사용 가능한 다른 연결로 대체 · 다음 달 1일 00:00 KST 초기화</p>}
            </div>
            {c.customerId && (
              <Button size="sm" variant="secondary" onClick={() => onOpenCustomer(c.customerId!)}>
                연결 확인
              </Button>
            )}
          </div>
        ))}
        {!warnings.length && <div className="seo-pad">선택 범위에 한도 경고가 없습니다.</div>}
      </Panel>
      <CallsChart customerId={customerId === "all" ? undefined : customerId} />
      <Panel title="고객사별 수집 사용량" extra={<Badge>{month}</Badge>}>
        {usage.isLoading ? (
          <Loading />
        ) : usage.error ? (
          <ErrorBox error={errorMessage(usage.error)} onRetry={() => void usage.refetch()} />
        ) : !rows.length ? (
          <Empty>이 달에 수집 기록이 있는 고객사가 없습니다.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>고객사</th>
                <th>수집 호출</th>
                <th>자기 키 / 빌린 키</th>
                <th>빌려준 호출</th>
                <th>오늘</th>
                <th>실패</th>
                <th>추적 / 실패 키워드</th>
                <th>마지막 수집</th>
                <th>플랫폼 연결</th>
                <th>자기 키 한도 사용률</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const own = (conns.data ?? []).filter((c) => c.customerId === u.customerId && c.platform === "naver_search" && c.status !== "disabled");
                return (
                  <tr key={u.customerId} onClick={() => onOpenCustomer(u.customerId)}>
                    <td>
                      <button
                        type="button"
                        className="seo-text-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenCustomer(u.customerId);
                        }}
                      >
                        {u.customerName}
                      </button>
                    </td>
                    <td>
                      <strong>{num(u.callsMonth)}</strong>
                    </td>
                    <td>
                      {num(u.ownKeyCalls)} / {num(u.borrowedCalls)}
                    </td>
                    <td>{num(u.lentCalls)}</td>
                    <td>{isCurrent ? num(u.callsToday) : "—"}</td>
                    <td>{num(u.errorsMonth)}</td>
                    <td>
                      {u.trackingKeywords} / {u.failingKeywords}
                    </td>
                    <td>{u.lastCollectedAt ? fmtDateTime(u.lastCollectedAt) : "—"}</td>
                    <td>
                      {platformOrder
                        .filter((p) => u.connections[p])
                        .map((p) => (
                          <div className="seo-key-hint" key={p}>
                            <small>{platformNames[p]}</small>
                            <ConnectionBadge status={u.connections[p]!} />
                          </div>
                        ))}
                      {!Object.keys(u.connections ?? {}).length && <Badge tone="slate">미등록</Badge>}
                    </td>
                    <td>
                      <div className="seo-usage-quota">
                        {own.map((c) => (
                          <div key={c.id}>
                            <small>
                              {c.label} · •••• {(c.credentialHint ?? "").replace(/^…/, "")}
                            </small>
                            <div className="seo-track">
                              <span style={{ width: `${Math.min(100, c.usedMonthPct ?? 0)}%` }} />
                            </div>
                            <small>
                              {num(c.usedMonth)} / {num(c.monthlyLimit)} · {(c.usedMonthPct ?? 0).toFixed(1)}%
                            </small>
                          </div>
                        ))}
                        {!own.length && "등록된 검색 키 없음"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        <div className="seo-table-foot">
          <p className="seo-caption">
            수집 호출 = 자기 키 + 빌린 키. 빌려준 호출은 별도 집계합니다. 키 확인 호출은 키 사용량에만 포함합니다. 한도 막대는 이번 달 기준입니다. 고객사 이름을 누르면
            연결 관리로 이동합니다.
          </p>
        </div>
      </Panel>
      <Panel title="마지막 수집 실패" extra={<Badge tone={(failures.data?.items.length ?? 0) ? "amber" : "teal"}>{failures.data?.total ?? 0}건</Badge>}>
        {failures.isLoading ? (
          <Loading />
        ) : failures.error ? (
          <ErrorBox error={errorMessage(failures.error)} onRetry={() => void failures.refetch()} />
        ) : (
          <FailureTable rows={(failures.data?.items ?? []).filter((x) => customerId === "all" || x.customerId === customerId)} onOpenCustomer={onOpenCustomer} />
        )}
        <div className="seo-table-foot">
          <p className="seo-caption">실패한 키워드는 다음 주기에 다시 수집합니다. 그동안 마지막 성공 결과를 유지합니다.</p>
        </div>
      </Panel>
    </div>
  );
}

function failureKind(err: string): string {
  if (/연결/.test(err)) return "연결 없음";
  if (/429|한도/.test(err)) return "한도 초과";
  if (/거부|401|403/.test(err)) return "키 거부";
  if (/시간 초과/.test(err)) return "응답 시간 초과";
  return "수집 실패";
}

function FailureTable({ rows, onOpenCustomer }: { rows: SeoCollectFailureRow[]; onOpenCustomer: (customerId: string) => void }) {
  if (!rows.length) return <div className="seo-pad">선택 범위에 수집 실패 키워드가 없습니다.</div>;
  return (
    <Table>
      <thead>
        <tr>
          <th>고객사 / 키워드</th>
          <th>사유</th>
          <th>마지막 시도</th>
          <th>조치</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const kind = failureKind(r.lastCollectError);
          return (
            <tr key={r.keywordId} onClick={() => onOpenCustomer(r.customerId)}>
              <td>
                <strong>{r.keyword}</strong>
                <small className="seo-meta">{r.customerName}</small>
              </td>
              <td>
                <Badge tone="amber">{kind}</Badge>
                <small className="seo-meta">{r.lastCollectError}</small>
              </td>
              <td>{r.lastCollectedAt ? fmtDateTime(r.lastCollectedAt) : "—"}</td>
              <td>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenCustomer(r.customerId);
                  }}
                >
                  {kind === "연결 없음" ? "연결 등록" : "연결 확인"}
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

/* ---------- 시스템 설정 › 수집기 ---------- */

export function SeoCollectorSettings({ me, onUsage }: { me: Me; onUsage: () => void }) {
  const settings = useSettings();
  const editable = can(me, "BILLING_MANAGE");
  const [msg, setMsg] = useState<string | null>(null);
  const save = useAction((v: { key: string; value: unknown }) => api.put(`/admin/settings/${v.key}`, { value: v.value }), [keys.settings, ["seo"]]);
  const policy = policyOf(settings.data);
  const fields = [
    ["seo_collect_interval_hours", "수집 주기 (시간)", 1, 168, policy.interval],
    ["seo_collect_batch", "10분당 키워드 수", 1, 5000, policy.batch],
    ["seo_collect_depth", "상위 결과 수", 10, 100, policy.depth],
    ["seo_quota_warn_pct", "한도 경고 (%)", 1, 100, policy.warn],
    ["seo_result_retention_days", "결과 보관 (일)", 7, 730, policy.retention],
  ] as const;
  return (
    <div className="seo-stack">
      <SeoConnections me={me} customerId="shared" />
      <Panel title="자동 수집 정책" extra={<Badge>중앙 스케줄러</Badge>}>
        {settings.isLoading ? (
          <Loading />
        ) : (
          <form
            key={JSON.stringify(policy)}
            className="seo-pad mcc-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!editable) return;
              const d = new FormData(e.currentTarget);
              const changed = fields.filter(([k, , , , cur]) => Number(d.get(k)) !== cur);
              Promise.all(changed.map(([k]) => save.mutateAsync({ key: k, value: Number(d.get(k)) })))
                .then(() => setMsg(changed.length ? "수집 정책을 저장했습니다. 30초 안에 반영됩니다." : "바뀐 값이 없습니다."))
                .catch((er) => setMsg(errorMessage(er)));
            }}
          >
            <div className="seo-ops-form-grid">
              {fields.map(([name, label, min, max, value]) => (
                <Field key={name} name={name} label={label} type="number" min={min} max={max} defaultValue={value} disabled={!editable} required />
              ))}
            </div>
            <p className="seo-caption">지역 검색은 상위 5개로 고정됩니다. 설정 키: {SEO_POLICY_KEYS.join(", ")}.</p>
            <div className="seo-routing">
              <ShieldCheck size={18} />
              <div>
                <strong>연결 선택 순서</strong>
                <p>대상 고객사 연결 → 빌려주기 허용 연결 (남은 월 한도가 많은 순) → Meta Crew 공용 연결</p>
                <small>키 거부·한도 실패 시 다음 연결로 한 번 더 시도합니다. 사용 가능한 연결이 없으면 키워드에 ‘연결 없음’을 남깁니다.</small>
              </div>
            </div>
            <DialogActions
              secondary={{ label: "SEO 수집 보기", onClick: onUsage }}
              primary={editable ? { label: "수집 정책 저장", type: "submit", disabled: save.isPending } : undefined}
              note={msg ?? undefined}
            />
          </form>
        )}
      </Panel>
    </div>
  );
}
